import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'

// Contexts y Hooks
import { I18nProvider } from './hooks/useI18n'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider } from './providers/AuthProvider'
import { Toaster } from 'react-hot-toast';
// Auth Store (Zustand)
import { useAuthStore } from './stores/authStore'

// Layouts
import DashboardLayout from './pages/dashboard/DashboardLayout'

// Componentes de Auth
import Login from './components/Auth/Login'
import Register from './components/Auth/Register'

// Páginas
import DashboardHome from './pages/dashboard/DashboardHome'
import TechnicalChat from './components/TechnicalChat'
import KnowledgeBase from './components/KnowledgeBase'
import AdminDashboard from './components/AdminDashboard'
import UserDashboard from './components/UserDashboard'
import ConfigurationsPanel from './components/Configuration'
import Support from './components/Support'
import MyAccount from './components/MyAccount'
import TicketList from './components/Tickets/TicketList'
import CreateTicketWizard from './components/Tickets/CreateTicketWizard'
import TicketDetail from './components/Tickets/TicketDetail'
import Docs from './pages/Docs'
import FAQs from './pages/FAQs'
import Usuarios from './pages/Usuarios'
import Pagos from './pages/Pagos'
import Planes from './pages/Planes'
import BusquedaNP from './pages/BusquedaNP'
import Monitoring from './pages/Monitoring'
// NOTA: InactivityWarningModal ya NO se importa aquí.
// AuthProvider lo renderiza internamente. Tenerlo dos veces causa flood de requests.

// =====================================================
// COMPONENTE DE RUTA PROTEGIDA CON ROLES
// =====================================================

interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles?: string[]
  requirePremiumForRoles?: string[]
}

function ProtectedRoute({ children, allowedRoles, requirePremiumForRoles }: ProtectedRouteProps) {
  const { user, isAuthenticated, checkAuth, clearAuth } = useAuthStore()

  // ✅ Verificar token real, no solo el flag persistido
  const isValid = isAuthenticated && checkAuth()

  if (!isValid) {
    console.warn('🚫 Token inválido o expirado, redirigiendo a /login')
    return <Navigate to="/login" replace />
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = user?.role?.toLowerCase().trim()
    const hasAccess = allowedRoles.some(role => role.toLowerCase() === userRole)
    const hasRole = allowedRoles.some(role => role.toLowerCase() === userRole)
    
    if (!hasAccess) {
      console.warn(`ðŸš« Acceso denegado. Usuario: ${userRole}, Roles requeridos:`, allowedRoles)
      return <Navigate to="/dashboard" replace />
    }
    
    if (!hasRole) return <Navigate to="/dashboard" replace />

    if (requirePremiumForRoles?.some(r => r.toLowerCase() === userRole)) {
      if (!user?.is_premium) return <Navigate to="/dashboard" replace />
    }
  
    
  }

  return <>{children}</>
}

// =====================================================
// LOGIN WRAPPER - CON SESIÓN ÚNICA
// =====================================================

const LoginWrapper: React.FC = () => {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()

  const handleLogin = async (email: string, password: string, rememberMe: boolean) => {
    try {
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      
      console.log('🔑 [LOGIN] Intentando login...', { email })

      // Capturar información del dispositivo
      const deviceInfo = {
        userAgent: navigator.userAgent,
        browser: navigator.userAgentData?.brands?.[0]?.brand || 'Unknown',
        platform: navigator.platform,
      }
      
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, deviceInfo }),
      })

      const data = await response.json()
      console.log('📦 [LOGIN] Respuesta del servidor:', data)

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Error en el login')
      }

      // Manejar formato con sessionId y refreshToken
      if (data.accessToken && data.user) {
        console.log('✅ [LOGIN] Login exitoso')
        
        const userData = {
          id: String(data.user.id),
          email: data.user.email,
          name: data.user.name,
          role: data.user.role || 'user',
          subscription_status: data.user.subscription_status,
          subscription_type: data.user.subscription_type,
          is_premium: data.user.is_premium,
        }

        console.log('👤 [LOGIN] Usuario:', userData.name, 'Rol:', userData.role)
        
        // Guardar con el nuevo formato (incluye refreshToken y sessionId)
        setAuth(userData, data.accessToken, data.refreshToken, data.sessionId)
        
        await new Promise(resolve => setTimeout(resolve, 200))

        console.log('🚀 [LOGIN] Navegando a /dashboard...')
        navigate('/dashboard', { replace: true })

        return data
      } 
      // Mantener compatibilidad con formato antiguo
      else if (data.success && data.token && data.user) {
        const userData = {
          id: String(data.user.id),
          email: data.user.email,
          name: data.user.name,
          role: data.user.role || 'user',
          subscription_status: data.user.subscription_status,
          subscription_type: data.user.subscription_type,
          is_premium: data.user.is_premium,
        }

        setAuth(userData, data.token, '', '') // Sin refresh/session
        navigate('/dashboard', { replace: true })
        return data
      } 
      else {
        throw new Error('Respuesta inválida del servidor')
      }
    } catch (error) {
      console.error('❌ [LOGIN] Error:', error)
      throw error
    }
  }

  return <Login onLogin={handleLogin} />
}

// =====================================================
// COMPONENTE PRINCIPAL APP 
// =====================================================

function App() {
  const { isAuthenticated } = useAuthStore()

  return (
    <ThemeProvider>
     <Toaster position="top-right" />
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* RUTAS PÚBLICAS */}
              <Route 
                path="/login" 
                element={!isAuthenticated ? <LoginWrapper /> : <Navigate to="/dashboard" replace />} 
              />

              <Route 
                path="/register" 
                element={!isAuthenticated ? <Register /> : <Navigate to="/dashboard" replace />} 
              />

              {/* RUTAS PROTEGIDAS */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardHome />} />
                
                <Route path="chat" element={<TechnicalChat />} />
               <Route
                    path="busqueda-np"
                    element={
                      <ProtectedRoute
                        allowedRoles={['admin', 'technician', 'user']}
                        requirePremiumForRoles={['user']}
                      >
                        <BusquedaNP />
                      </ProtectedRoute>
                    }
                  />
                <Route path="knowledge" element={<KnowledgeBase />} />
                <Route path="settings" element={<ConfigurationsPanel />} />
                <Route path="support" element={<Support />} />
                <Route path="account" element={<MyAccount />} />
                
                <Route path="tickets" element={<TicketList />} />
                <Route path="tickets/create" element={<CreateTicketWizard />} />
                <Route path="tickets/:id" element={<TicketDetail />} />
                
                <Route path="docs" element={<Docs />} />
                <Route path="faqs" element={<FAQs />} />
                
                 {/* ADMIN Y TECHNICIAN */}
                 <Route
                  path="monitoring"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'technician']}>
                      <Monitoring />
                    </ProtectedRoute>
                  }
                />

                {/* ADMIN Y TECHNICIAN */}
                <Route
                  path="admin"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'technician']}>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                
                {/* SOLO ADMIN */}
                <Route
                  path="users"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Usuarios />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="payments"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Pagos />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="plans"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <Planes />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="system"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <ConfigurationsPanel />
                    </ProtectedRoute>
                  }
                />
              </Route>
              
              {/* REDIRECCIÓN POR DEFECTO */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </ThemeProvider>
  )
}

export default App